/**
 * 1-Klick-Onboarding-Dialog für Affiliates.
 * - Nicht eingeloggt: leitet zum Login mit Redirect.
 * - Eingeloggt: 2 Felder (Code + Checkbox), Insert mit status='active', dann Redirect zu /partner.
 * - Schon ein Partnerkonto, nicht aktiv: zeigt das feste Empfehlungswort und den Weg zum Team (M2, 19.10.2026).
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useMyAffiliate } from "@/hooks/useMyAffiliate";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function slugify(value: string): string {
  return value
    .toLowerCase()
    .replace(/ä/g, "ae").replace(/ö/g, "oe").replace(/ü/g, "ue").replace(/ß/g, "ss")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40);
}

const BecomePartnerDialog = ({ open, onOpenChange }: Props) => {
  const navigate = useNavigate();
  const { data: user, isLoading: userLoading } = useCurrentUser();
  const { data: existing, refetch } = useMyAffiliate(user?.id);
  const [code, setCode] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open && !code && user?.email) {
      setCode(slugify(user.email.split("@")[0]));
    }
  }, [open, user?.email, code]);

  // Bereits aktiv → direkt weiterleiten
  useEffect(() => {
    if (open && existing?.status === "active") {
      onOpenChange(false);
      navigate("/partner");
    }
  }, [open, existing, navigate, onOpenChange]);

  // Nach Login/Registrierung zurück zur Landingpage + Dialog automatisch wieder öffnen
  const redirectTarget = encodeURIComponent("/partner-werden?intent=open");

  const handleRegister = () => {
    onOpenChange(false);
    navigate(`/registrieren?redirect=${redirectTarget}`);
  };

  const handleLogin = () => {
    onOpenChange(false);
    navigate(`/login?redirect=${redirectTarget}`);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.id) return handleLogin();
    if (!/^[a-z0-9-]{3,40}$/.test(code)) {
      toast({ title: "Ungültiger Code", description: "3–40 Zeichen, nur Kleinbuchstaben, Ziffern und Bindestriche.", variant: "destructive" });
      return;
    }
    if (!agreed) {
      toast({ title: "Bitte zustimmen", description: "Bitte akzeptiere die Partnerbedingungen.", variant: "destructive" });
      return;
    }

    // Schon ein Partnerkonto (pausiert, gesperrt): Das Empfehlungswort bleibt fest, geteilte Links tragen es (M2).
    // Der Trigger `affiliates_guard_self_update` setzte eine Änderung still zurück; die Seite bietet sie nicht mehr an.
    if (existing) return;

    setSubmitting(true);
    try {
      // Insert mit status='pending' — DB-Trigger aktiviert sofort auf 'active'
      // (auto-approval) und blockiert Self-Manipulation von Provisionsrate etc.
      const { error } = await supabase.from("affiliates").insert({
        user_id: user.id,
        affiliate_code: code,
        status: "pending",
      });
      if (error) {
        if (error.code === "23505") {
          toast({ title: "Code bereits vergeben", description: "Bitte wähle einen anderen Code.", variant: "destructive" });
          return;
        }
        throw error;
      }
      // Welcome-Mail an Partner + Admin (Fehler nicht blockierend)
      try {
        await supabase.functions.invoke("send-partner-email", {
          body: { type: "welcome", userId: user.id, affiliateCode: code },
        });
      } catch (mailErr) {
        console.warn("[BecomePartnerDialog] Welcome-Mail Fehler", mailErr);
      }
      toast({ title: "Willkommen im Partnerprogramm!", description: "Dein Account ist freigeschaltet." });
      await refetch();
      onOpenChange(false);
      navigate("/partner");
    } catch (err) {
      toast({ title: "Fehler", description: (err as Error).message, variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Partner werden</DialogTitle>
          <DialogDescription>
            In nur einem Schritt: Wähle dein persönliches Empfehlungswort. Daraus entsteht dein eigener Link, den du an Kolleginnen und Freunde weitergeben kannst. Für jede Anmeldung über deinen Link bekommst du 20 % Provision.
          </DialogDescription>
        </DialogHeader>

        {userLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : !user ? (
          <div className="space-y-4 py-2">
            <p className="text-sm text-muted-foreground">
              Du brauchst nur einen kostenlosen MusiKonzept-Account, um direkt loszulegen. Wir bringen dich danach automatisch zurück zur Code-Auswahl.
            </p>
            <div className="space-y-2">
              <Button onClick={handleRegister} className="w-full">
                Kostenlosen Account erstellen
              </Button>
              <Button onClick={handleLogin} variant="outline" className="w-full">
                Ich habe schon einen Account
              </Button>
            </div>
          </div>
        ) : existing ? (
          <div className="space-y-3 py-2 text-sm">
            <p>
              Du hast schon ein Partnerkonto. Dein Empfehlungswort ist{" "}
              <span className="font-mono font-semibold text-primary">{existing.affiliate_code}</span>. Es bleibt dasselbe,
              damit deine Links weiter funktionieren.
            </p>
            <p className="text-muted-foreground">
              Dein Partnerkonto ist gerade nicht aktiv. Schreib uns kurz über das Kontaktformular, dann schalten wir es
              wieder frei.
            </p>
            <Button
              variant="outline"
              className="w-full"
              onClick={() => {
                onOpenChange(false);
                navigate("/kontakt");
              }}
            >
              Zum Kontaktformular
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="affiliate-code">Dein persönliches Empfehlungswort</Label>
              <Input
                id="affiliate-code"
                value={code}
                onChange={(e) => setCode(e.target.value.toLowerCase())}
                placeholder="dein-name"
                autoComplete="off"
              />
              <p className="mt-2 text-xs text-muted-foreground">
                Das wird dein persönlicher Link – diesen teilst du später mit anderen:
              </p>
              <p className="mt-1 text-sm font-medium text-primary">
                musikonzept.de/?ref=<span className="font-mono">{code || "dein-name"}</span>
              </p>
            </div>

            <label className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                className="mt-1"
              />
              <span>
                Ich bin mit den Partnerbedingungen einverstanden: 20 % Provision auf jede Zahlung für alles, was über meinen Link gebucht wird (Mitgliedschaft, Kompakt, Musiktaler, Kurse, Termine und Ausbildungen).
              </span>
            </label>

            <DialogFooter>
              <Button type="submit" disabled={submitting} className="w-full">
                {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Jetzt aktivieren
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default BecomePartnerDialog;
